import { useCallback, useEffect, useRef } from 'react'
import { AthleteDatasourcesQuery } from 'api/integrations/storyblok/generated/cms'
import { useAthleteListingQuery } from 'apollo/generated/graphqlClient'
import { useRouter } from 'next/router'
import { Spinner } from 'components/common/Spinner'
import { AthleteCard } from '../AthleteCard'
import { collegeGroups } from '../AthleteFilter'
import { FresnoAthleteFilter } from './FresnoAthleteFilter'

const DEFAULT_LIMIT = 100

export interface AthleteListingProps {
  athleteDatasources: AthleteDatasourcesQuery
}

export interface AthletePageQuery {
  sport?: string
  position?: string
  sortBy?: string
  collegeName?: string
  from?: string
  to?: string
}

export const FresnoAthletesPage = ({ athleteDatasources }: AthleteListingProps) => {
  const router = useRouter()
  const { sport, position, sortBy, from, to } = router.query as AthletePageQuery
  const observerElement = useRef<HTMLDivElement | null>(null)

  const {
    data: athletesData,
    fetchMore,
    loading: isLoading,
  } = useAthleteListingQuery({
    variables: {
      athleteListingInput: {
        limit: DEFAULT_LIMIT,
        page: 1,
        sport: sport || '',
        position: position || '',
        sortBy: sortBy || '',
        collegeName: collegeGroups['Fresno, CA'].join(),
        availability: from && to ? { from, to } : undefined,
      },
    },
    notifyOnNetworkStatusChange: true,
  })

  const athletes = athletesData?.athleteListing.items || []

  const fetchMoreData = useCallback(
    ({ athletesLength, total }: { athletesLength: number; total: number }) => {
      if (isLoading) {
        return
      }
      if (total > athletesLength) {
        void fetchMore({
          variables: {
            athleteListingInput: {
              limit: DEFAULT_LIMIT,
              page: Math.floor(athletesLength / DEFAULT_LIMIT) + 1,
              sport: sport || '',
              position: position || '',
              collegeName: collegeGroups['Fresno, CA'].join(),
              sortBy: sortBy || '',
              availability: from && to ? { from, to } : undefined,
            },
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) {
              return prev
            }

            return {
              athleteListing: {
                total: fetchMoreResult.athleteListing.total,
                items: [...prev.athleteListing.items, ...fetchMoreResult.athleteListing.items],
              },
            }
          },
        })
      }
    },
    [isLoading, fetchMore, sport, position, sortBy, from, to],
  )

  // intersection useEffect - handles infinite scroll fetch
  useEffect(() => {
    const observedElement = observerElement.current
    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        if (entries[0].isIntersecting) {
          fetchMoreData({
            athletesLength: athletes.length,
            total: athletesData?.athleteListing.total || 0,
          })
        }
      },
      { threshold: 0.1 },
    )

    if (observerElement.current) {
      observer.observe(observerElement.current)
    }

    return () => {
      if (observedElement) {
        observer.unobserve(observedElement)
      }
    }
  }, [athletes.length, athletesData?.athleteListing.total, fetchMoreData])

  return (
    <div className="min-h-[calc(100vh-96px)] lg:min-h-[calc(100vh-112px)] flex flex-col">
      <FresnoAthleteFilter athleteDatasources={athleteDatasources} />
      <div className="pb-32 mt-16 flex h-full flex-grow flex-col items-center w-full px-8 sm:px-12 md:px-16 lg:px-24">
        {athletes.length ? (
          <>
            <div className="grid xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-8 w-full max-w-[1250px]">
              {athletes.map((athlete) => (
                <AthleteCard
                  athleteDatasources={athleteDatasources}
                  key={athlete.slug}
                  athlete={athlete}
                />
              ))}
            </div>
            {isLoading && (
              <div className="mt-20">
                <Spinner />
              </div>
            )}
          </>
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>
            {isLoading ? (
              <div className="mt-20">
                <Spinner />
              </div>
            ) : (
              <div className="text-base text-slate-500 h-full my-auto">
                There are no athletes that match the specified criteria.
              </div>
            )}
          </>
        )}
        <div ref={observerElement} className="w-full h-1" />
      </div>
    </div>
  )
}
